import React, { useMemo } from 'react'
import { Trans } from '@lingui/react/macro'
import { useLazyLoadQuery, useFragment, graphql } from 'react-relay'
import {
  useParams,
  Routes,
  Route,
  useNavigate,
  useMatch,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { useScreenMessages } from '@data/i18n'

import { UserMetadata } from '@newComponents/User/UserMetadata'
import { YStack, XStack } from 'tamagui'

import { H2, Button, wrapSuspenseComponent, Link } from '@newComponents'
import { Group } from '@newComponents/Group'

import { UserEventsScreenQuery } from './__generated__/UserEventsScreenQuery.graphql'

const UserEventsUpcomingScreen = wrapSuspenseComponent(
  loadable(() => import('../UserEventsUpcomingScreen/UserEventsUpcomingScreen'))
)

const UserEventsPastScreen = wrapSuspenseComponent(
  loadable(() => import('../UserEventsPastScreen/UserEventsPastScreen'))
)

export const UserEventsScreen = () => {
  useScreenMessages((locale) => import(/* webpackChunkName: "locale-user-events-screen-[request]" */ `./locales/${locale}.po`))
  const params = useParams()

  const upcomingMatch = useMatch(`${params.actorId}/events/upcoming`)
  const pastMatch = useMatch(`${params.actorId}/events/past`)

  const data = useLazyLoadQuery<UserEventsScreenQuery>(
    graphql`
      query UserEventsScreenQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on User {
            firstName
            lastName

            ...UserMetadata_user
          }
        }
      }
    `,
    { id: params.actorId || '' }
  )

  const upcomingOrPast = useMemo(() => {
    if (upcomingMatch) {
      return 'upcoming'
    }

    if (pastMatch) {
      return 'past'
    }

    return 'upcoming'
  }, [upcomingMatch, pastMatch])

  if (data.nodeBySlugId?.__typename !== 'User') {
    return null
  }

  return (
    <>
      <UserMetadata
        user={data.nodeBySlugId}
        title={`${data.nodeBySlugId.firstName} ${
          data.nodeBySlugId.lastName
        }'s ${upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'} Events`}
      />

      <YStack gap='$6' flex={1}>
        <XStack alignItems='center' gap='$4'>
          <XStack flex={1}>
            <H2>
              {upcomingOrPast === 'upcoming' ? <Trans>Upcoming Events</Trans> : <Trans>Past Events</Trans>}
            </H2>
          </XStack>

          <Group activeIndex={upcomingOrPast === 'past' ? 1 : 0}>
            <XStack gap='$2'>
              <Group.Item index={0}>
                <Link
                  href={`/${params.actorId}/events/upcoming`}
                  size='$4'
                  fontWeight='$6'
                  color='$colorStrong'
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                  paddingVertical='$2'
                  paddingHorizontal='$3'
                >
                  <Trans>Upcoming</Trans>
                </Link>
              </Group.Item>

              <Group.Item index={1}>
                <Link
                  href={`/${params.actorId}/events/past`}
                  size='$4'
                  fontWeight='$6'
                  color='$colorStrong'
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                  paddingVertical='$2'
                  paddingHorizontal='$3'
                >
                  <Trans>Past</Trans>
                </Link>
              </Group.Item>
            </XStack>
          </Group>
        </XStack>

        <Routes>
          <Route index element={<UserEventsUpcomingScreen />} />

          <Route path='upcoming' element={<UserEventsUpcomingScreen />} />

          <Route path='past' element={<UserEventsPastScreen />} />
        </Routes>
      </YStack>
    </>
  )
}

export default UserEventsScreen
